import { createTheme, type Theme } from '@mui/material/styles';

import { resolveBrandPalette } from '../../lib/brand-color-accessibility';
import { FALLBACK_THEME } from '../utils/fallback';
import type { WidgetConfig, ChatAgentFontSize } from '../hooks/useFetchWidgetConfig';
import { CHAT_FONT_SCALE_FACTORS } from '../../lib/chat-customization';
import type { WidgetBrandColors } from './useWidgetBrandColors';
import { withLucideMuiDefaults } from '../../components/icons';

/** Scale factors for chat typography (sm=1.00, md=1.15, lg=1.30). Applied as global multiplier; output remains px. */
export const FONT_SCALE_FACTORS: Record<ChatAgentFontSize, number> = CHAT_FONT_SCALE_FACTORS;

export type WidgetThemeWithScale = Theme & {
  widgetTypographyScale: number;
  widgetBrand: WidgetBrandColors;
};

const DEFAULT_FONT_FAMILY = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(',');

/** WCAG 2.1 AAA normal text on #ffffff (~7.8:1). */
const ACCESSIBLE_TEXT_DISABLED = '#525252';
const ACCESSIBLE_TEXT_SECONDARY = '#616161';

// Avoid `rem` in widget styles. Host sites can change
// `html { font-size }`, and `rem` will follow the host regardless of Shadow DOM.
// Using `px` prevents external scaling from affecting our UI.
const pxToPx = (px: number) => `${px}px`;

const BASE_PALETTE = {
  mode: 'light' as const,
  success: {
    main: '#20e97a',
    light: '#6cb06c',
  },
  info: {
    main: '#98ceee',
  },
  background: {
    default: '#f9f9f9',
    paper: '#ffffff',
  },
  divider: 'rgba(0, 0, 0, 0.12)',
  grey: {
    300: '#e0e0e0',
  },
  text: {
    disabled: ACCESSIBLE_TEXT_DISABLED,
    secondary: ACCESSIBLE_TEXT_SECONDARY,
  },
};

export function buildWidgetThemeOptions(
  primary: string,
  secondary: string
): { theme: Omit<WidgetConfig['theme'], 'typography'>; widgetBrand: WidgetBrandColors } {
  const resolved = resolveBrandPalette(primary, secondary);

  return {
    theme: {
      palette: {
        ...BASE_PALETTE,
        primary: {
          ...resolved.primary,
          textOnLight: resolved.primaryOnPaper,
        },
        secondary: resolved.secondary,
      },
    },
    widgetBrand: {
      gradient: resolved.gradient,
      primaryOnPaper: resolved.primaryOnPaper,
    },
  };
}

function createWidgetThemeFromOptions(
  themeConfig: WidgetConfig['theme'],
  widgetBrand: WidgetBrandColors,
  fontSize: ChatAgentFontSize
): WidgetThemeWithScale {
  const scale = FONT_SCALE_FACTORS[fontSize];
  const theme = createTheme({
    ...themeConfig,
    components: withLucideMuiDefaults(themeConfig.components),
    typography: {
      fontFamily: DEFAULT_FONT_FAMILY,
      pxToRem: pxToPx,
    } as unknown as Theme['typography'],
  } as unknown as Parameters<typeof createTheme>[0]);

  return { ...theme, widgetTypographyScale: scale, widgetBrand } as WidgetThemeWithScale;
}

export function createFallbackWidgetTheme(): WidgetThemeWithScale {
  const { theme, widgetBrand } = buildWidgetThemeOptions(
    FALLBACK_THEME.primary,
    FALLBACK_THEME.secondary
  );
  return createWidgetThemeFromOptions(theme, widgetBrand, 'sm');
}

export function createConfigWidgetTheme(
  themeConfig: WidgetConfig['theme'],
  fontSize: ChatAgentFontSize = 'sm',
  widgetBrand?: WidgetBrandColors
): WidgetThemeWithScale {
  const primaryPalette = themeConfig.palette?.primary as
    | { main?: string; contrastText?: string }
    | undefined;
  const secondaryPalette = themeConfig.palette?.secondary as { main?: string } | undefined;
  const primaryMain = primaryPalette?.main ?? FALLBACK_THEME.primary;
  const secondaryMain = secondaryPalette?.main ?? FALLBACK_THEME.secondary;

  const { theme: builtTheme, widgetBrand: builtBrand } = buildWidgetThemeOptions(
    primaryMain,
    secondaryMain
  );

  const mergedTheme: WidgetConfig['theme'] = {
    ...themeConfig,
    palette: {
      ...themeConfig.palette,
      ...builtTheme.palette,
    },
  };

  return createWidgetThemeFromOptions(mergedTheme, widgetBrand ?? builtBrand, fontSize);
}

/** Returns fontSize in px scaled by widget typography scale (for use in sx or style). */
export function scaledFontSize(theme: Theme | WidgetThemeWithScale, basePx: number): number {
  const scale = (theme as WidgetThemeWithScale).widgetTypographyScale ?? 1;
  return Math.round(basePx * scale);
}
